import React, { type JSX, useState } from 'react';

import Icon from 'components/DesignSystem/Icon';
import Switch from 'components/DesignSystem/Switch';
import ProductShortList from 'components/Admin/Products/ShortList';
import StatusTag from 'components/Admin/Companies/Table/Row/StatusTag';
import EditButton from 'components/Admin/Companies/Table/Row/Actions/EditButton';
import { ListedCompanyContract, activityToggleMessage } from 'helper/companies';
import contractDate from 'utils/contractDate';
import useRequestToggle from 'utils/hooks/companies/useRequestToggle';
import useToggle from 'utils/hooks/useToggle';

import Details from './Details';

import './index.scss';

export default function CompanyCard({ company, setConfirmationDialogBox, onEditClick }: {
  company: ListedCompanyContract,
  setConfirmationDialogBox: (open: boolean, body: JSX.Element, confirm: () => void) => void,
  onEditClick: (id: number, contractId: number) => () => void,
}) {
  const [expanded, setExpanded] = useState(false);
  const { requestCompanyActivityToggle } = useRequestToggle({ companyId: company.empresa_id });
  const { toggled, callback: changeCompanyActivity } = useToggle(
    company.active,
    requestCompanyActivityToggle,
  );

  return (
    <div className="companies-mobile-card">
      <div className="companies-mobile-card-summary">
        <div className="companies-mobile-card-info">
          <p className="companies-mobile-card-name">{company.razao_social}</p>
          <p className="companies-mobile-card-line">
            <Icon iconType="time" size="small" />
            {`${contractDate(company.dt_inicio)} - ${contractDate(company.dt_fim)}`}
          </p>
        </div>
        <StatusTag status={company.status} />
      </div>

      <div className="companies-mobile-card-products">
        <ProductShortList products={company.products} />
      </div>

      <button
        type="button"
        className="companies-mobile-card-expand"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? 'Ver menos' : 'Ver detalhes'}
        <Icon iconType={expanded ? 'up_arrow' : 'down_arrow'} size="small" />
      </button>

      {expanded && (
        <div className="companies-mobile-card-details">
          <Details company={company} />
        </div>
      )}

      <div className="companies-mobile-card-actions">
        <div className="companies-mobile-card-toggle">
          <Switch
            className="companies-mobile-switch"
            checked={toggled}
            aria-label={`Atividade de ${company.razao_social}`}
            onChange={() => {
              setConfirmationDialogBox(
                true,
                activityToggleMessage(toggled),
                changeCompanyActivity,
              );
            }}
          />
          <span className={toggled ? 'companies-mobile-switch-on' : 'companies-mobile-switch-off'}>
            {toggled ? 'Ativa' : 'Inativa'}
          </span>
        </div>
        <div className="companies-mobile-card-buttons">
          <EditButton
            contractId={company.contrato_id}
            onEditClick={onEditClick(company.empresa_id, company.contrato_id)}
          />
        </div>
      </div>
    </div>
  );
}
